import "./DeleteCourseModal.scss";

import { FC, useState } from "react";

import { serverUnlinkCourse } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { LegacyButton } from "mds/components/LegacyButton";
import { showConfirmationModal } from "mds/components/Modal";
import { TextField } from "mds/components/TextField";
import { useAppSelector } from "store/index";
import { selectCurrentCourseCodeTitle, selectLmsType } from "store/selectors";
import { toastSuccessMessage } from "utils/alerts";

export const showUnlinkConfirmationModal = ({
  courseId,
  lmsType,
}: {
  courseId: string;
  lmsType: string;
}) => {
  return showConfirmationModal({
    title: t("confirm_unlink_modal.unlink_item", { lms: lmsType }),
    hideCancelConfirmButtons: true,
    children: (onClose) => <UnlinkCourseModal courseId={courseId} onClose={onClose} />,
  });
};

type UnlinkCourseModalProps = {
  courseId: string;
  onClose: () => void;
};

// eslint-disable-next-line react-refresh/only-export-components
const UnlinkCourseModal: FC<UnlinkCourseModalProps> = ({ courseId, onClose }) => {
  const lmsType = useAppSelector(selectLmsType);
  const courseCodeTitle = useAppSelector(selectCurrentCourseCodeTitle);
  const buttonText = t("confirm_unlink_modal.button_text");
  const [deletePagesAndAssessments, setDeletePagesAndAssessments] = useState<boolean>(true);
  const [disableUnlink, setDisableUnlink] = useState<boolean>(true);

  const handleCourseCodeConfirmationCheck = (text: string) => {
    setDisableUnlink(text !== courseCodeTitle);
  };

  const handleUnlink = async () => {
    await serverUnlinkCourse(courseId, deletePagesAndAssessments);
    toastSuccessMessage(t("success.toasts.unlink_course_success"));
    onClose();
  };

  return (
    <div className="course-delete-modal mt-8 flex flex-col items-start gap-8">
      <div className="course-delete-warning">
        {t("confirm_unlink_modal.unlink_course_warning", { lms: lmsType })}
      </div>

      <label className="flex items-start gap-2">
        <input
          checked={deletePagesAndAssessments}
          className="mt-[0.1875rem]"
          type="checkbox"
          onChange={() => setDeletePagesAndAssessments(!deletePagesAndAssessments)}
        />
        <div>{t("confirm_unlink_modal.clear_content_checkbox_text", { lms: lmsType })}</div>
      </label>
      <div className="flex flex-col gap-2">
        <label htmlFor="course-code">{t("course_list_panel.course_code_confirm_label")}</label>

        <TextField
          id="course-code"
          placeholder={t("course_list_panel.course_code_placeholder")}
          isEditing
          onChange={(event) => handleCourseCodeConfirmationCheck(event.target.value)}
        />
      </div>

      <div className="flex w-full justify-end gap-2">
        <LegacyButton kind="secondary" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </LegacyButton>

        <LegacyButton
          disabled={disableUnlink}
          kind="destructive"
          title={buttonText}
          onClick={handleUnlink}
        >
          {buttonText}
        </LegacyButton>
      </div>
    </div>
  );
};
